import { useState } from "react";
import { normalizeSerial, normalizeTracking, todayKst } from "@rma-orbit/shared";
import type {
  CaseDetail as Case,
  CaseBatchIntakeResponse,
  CaseDeleteResponse,
  Company,
  MailDraft,
  MailType,
  ShipmentBatchResponse,
} from "@rma-orbit/shared";
import { api } from "../api/client.js";
import { navigate } from "../App.js";
import {
  Busy,
  CASE_STATUS_OPTIONS,
  Cell,
  ErrorNotice,
  Notice,
  ResultBadge,
  StatusBadge,
  useAsync,
} from "../lib/ui.js";
import { downloadRowsAsXlsx } from "../lib/excel.js";
import { copyRichText } from "../lib/clipboard.js";

/**
 * 케이스 화면 (harness/UI-SPEC.md §1).
 * 목록/검색 · 배치 입고(붙여넣기 → 정규화 미리보기) · 체크박스 다중 선택 → 일괄 승인 ·
 * 엑셀 다운로드.
 */

type Filters = {
  status: string;
  company: string;
  serial: string;
  from: string;
  to: string;
  swap: string;
  approvalPending: boolean;
};
const EMPTY: Filters = {
  status: "",
  company: "",
  serial: "",
  from: "",
  to: "",
  swap: "",
  approvalPending: false,
};

export function Cases({
  can,
  initialSwap,
}: {
  can: (p: string) => boolean;
  initialSwap?: "any" | "none";
}) {
  // 대시보드의 [★ 교체 대기] 에서 넘어오면 그 조건이 걸린 채로 열린다
  const start: Filters = initialSwap ? { ...EMPTY, status: "AT_VENDOR", swap: initialSwap } : EMPTY;
  const [filters, setFilters] = useState<Filters>(start);
  const [applied, setApplied] = useState<Filters>(start);
  // 목록 위의 빠른 필터 — 상태 칩은 서버 조건을 바꾸고, 글자 필터는 받은 페이지 안에서만 거른다
  const [quick, setQuick] = useState("");
  // 날짜별 보기 — 입고일로 묶어 날짜 머리줄을 끼운다. 기본은 켜 둔다 (2026-09-09 대표 지시)
  const [groupByDate, setGroupByDate] = useState(true);

  const pad = (n: number) => String(n).padStart(2, "0");
  const dayOf = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  // 날짜 칩 — 서버 조건(from·to)을 바꾼다. 기준은 KST 오늘
  function applyRange(key: string) {
    const today = todayKst();
    let from = "";
    let to = "";
    if (key === "1") { from = today; to = today; }
    else if (key === "7") { const d = new Date(`${today}T00:00:00`); d.setDate(d.getDate() - 6); from = dayOf(d); to = today; }
    else if (key === "month") { from = `${today.slice(0, 7)}-01`; to = today; }
    else if (key === "prevMonth") {
      const [y, m] = today.split("-").map(Number);
      from = dayOf(new Date(y!, m! - 2, 1));
      to = dayOf(new Date(y!, m! - 1, 0));
    }
    const next: Filters = { ...filters, from, to };
    setFilters(next);
    setApplied(next);
  }
  const rangeKey = (() => {
    const today = todayKst();
    if (!applied.from && !applied.to) return "all";
    if (applied.from === today && applied.to === today) return "1";
    const week = new Date(`${today}T00:00:00`); week.setDate(week.getDate() - 6);
    if (applied.from === dayOf(week) && applied.to === today) return "7";
    if (applied.from === `${today.slice(0, 7)}-01` && applied.to === today) return "month";
    const [y, m] = today.split("-").map(Number);
    if (applied.from === dayOf(new Date(y!, m! - 2, 1)) && applied.to === dayOf(new Date(y!, m! - 1, 0))) return "prevMonth";
    return "custom";
  })();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [message, setMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [deleteReport, setDeleteReport] = useState<CaseDeleteResponse | null>(null);
  const [mail, setMail] = useState<MailDraft | null>(null);
  const [shipOpen, setShipOpen] = useState(false);
  const [shipReport, setShipReport] = useState<ShipmentBatchResponse | null>(null);

  const companies = useAsync(() => api.companies(), []);
  const list = useAsync(
    () =>
      api.cases({
        status: applied.status,
        company: applied.company,
        serial: applied.serial,
        from: applied.from,
        to: applied.to,
        swap: applied.swap === "" ? undefined : (applied.swap as "any" | "none"),
        approval_pending: applied.approvalPending ? 1 : undefined,
        limit: 200,
      }),
    [JSON.stringify(applied)],
  );

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  /** 체크박스 다중 선택 → [일괄 승인] 한 번 (APPROVAL-POLICY §4) */
  async function approveSelected(kind: "SHIP_TO_VENDOR" | "SHIP_TO_OWNER") {
    const ids = [...selected];
    if (ids.length === 0) return;
    const note = window.prompt(
      `${ids.length}건을 승인합니다. 승인 메모(선택) — 예: "2026-09-09 부장님 구두 승인"`,
      "",
    );
    if (note === null) return;

    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      const approvals = await api.createApprovals(ids, kind);
      const decided = await api.decideApprovals(
        approvals.map((a) => a.id),
        "APPROVED",
        note || undefined,
      );
      setMessage(`${decided.length}건 승인했습니다. 이제 출고를 등록할 수 있습니다.`);
      setSelected(new Set());
      list.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 선택한 건 삭제 (API-SPEC §2-3).
   * 못 지운 건은 **이유를 그대로 보여 준다** — "3건 중 1건 삭제" 만 말하면
   * 나머지 2건이 왜 남았는지 아무도 모른 채 지운 줄 알고 넘어간다.
   */
  async function deleteSelected() {
    const ids = [...selected];
    if (ids.length === 0) return;
    const reason = window.prompt(
      `${ids.length}건을 지웁니다. 되돌릴 수 없습니다.
지우는 이유를 적어 주세요 (감사 기록에 남습니다).`,
      "",
    );
    if (reason === null) return;
    if (reason.trim().length < 2) {
      setActionError(new Error("지우는 이유를 2자 이상 적어 주세요."));
      return;
    }

    setBusy(true);
    setActionError(null);
    setMessage(null);
    try {
      const result = await api.deleteCases(ids, reason.trim());
      setDeleteReport(result);
      setMessage(
        result.blocked.length === 0
          ? `${result.deleted.length}건을 지웠습니다.`
          : `${result.deleted.length}건을 지웠고, ${result.blocked.length}건은 지우지 않았습니다.`,
      );
      setSelected(new Set());
      list.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  /**
   * 부장님 보고 메일 초안 (MAIL-TEMPLATE §2).
   * 만들기만 한다 — 발송은 사람이 그룹웨어에서 한다.
   */
  async function makeMail(type: MailType) {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    setActionError(null);
    setMessage(null);
    setDeleteReport(null);
    try {
      setMail(await api.mailDraft(ids, type));
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  async function download(withPii: boolean) {
    setBusy(true);
    setActionError(null);
    try {
      const result = await api.exportCases({ ...applied, preset: "manager", pii: withPii });
      if (result.rows.length === 0) {
        setMessage("내려받을 건이 없습니다.");
        return;
      }
      const stamp = todayKst();
      await downloadRowsAsXlsx(result.rows, `RMA_보고_${stamp}.xlsx`);
      setMessage(`${result.rows.length}건을 내려받았습니다.`);
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(false);
    }
  }

  // 한 줄. 날짜별 보기에서 머리줄 사이에 끼우려고 따로 뺐다
  const rowOf = (row: Case) => (
      <tr key={row.id}>
        <td>
          <input
            type="checkbox"
            checked={selected.has(row.id)}
            onChange={() => toggle(row.id)}
            aria-label={`${row.case_no} 선택`}
          />
        </td>
        <td>
          <a
            href={`#/cases/${row.id}`}
            onClick={(e) => {
              e.preventDefault();
              navigate(`#/cases/${row.id}`);
            }}
          >
            {row.case_no}
          </a>
        </td>
        <td className="num">{row.received_at}</td>
        <td className="num">
          {row.shipped_at ? (
            <span title={row.shipped_to ?? undefined}>
              {row.shipped_at}
              {row.shipped_to && <span className="muted" style={{ fontSize: 11 }}> → {row.shipped_to}</span>}
            </span>
          ) : (
            <span className="empty">—</span>
          )}
        </td>
        <td className="mono">{row.serial_norm}</td>
        <td>
          <StatusBadge status={row.status} />
        </td>
        <td>
          <ResultBadge result={row.result} />
        </td>
        <td>
          <Cell value={row.reported_symptom} />
        </td>
        {/* ★ 제1 목적. 목록에서 바로 보여야 연결이 실무에 남는다 */}
        <td className="mono">
          {row.replacement_serial ? (
            <a
              href={`#/devices/${encodeURIComponent(row.replacement_serial)}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`#/devices/${encodeURIComponent(row.replacement_serial!)}`);
              }}
            >
              {row.replacement_serial}
            </a>
          ) : (
            <span className="empty">—</span>
          )}
        </td>
        <td>
          {row.reentry_of_case_id ? (
            <span className="badge" title={`이전 케이스 ${row.reentry_of_case_no ?? `#${row.reentry_of_case_id}`}`}>
              재입고
            </span>
          ) : (
            <span className="empty">—</span>
          )}
        </td>
      </tr>
  );

  return (
    <>
      <div className="row" style={{ alignItems: "flex-end", marginBottom: 14 }}>
        <div className="grow">
          <h1>케이스 목록</h1>
          <p className="page-desc" style={{ margin: 0 }}>장비 한 대의 왕복 여정 1건이 케이스입니다.</p>
        </div>
        {can("case.write") && (
          <button className="primary" onClick={() => navigate("#/intake")}>
            + 입고 접수
          </button>
        )}
      </div>

      <div className="card">
        <h2>검색</h2>
        <div className="row">
          <label className="field">
            상태
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            >
              <option value="">전체</option>
              {CASE_STATUS_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            업체
            <select
              value={filters.company}
              onChange={(e) => setFilters({ ...filters, company: e.target.value })}
            >
              <option value="">전체</option>
              {(companies.data ?? []).map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            시리얼
            <input
              value={filters.serial}
              placeholder="일부만 넣어도 됩니다 (원·교체 시리얼)"
              onChange={(e) => setFilters({ ...filters, serial: e.target.value })}
            />
          </label>
          <label className="field">
            승인 대기
            <select
              value={filters.approvalPending ? "1" : ""}
              onChange={(e) => setFilters({ ...filters, approvalPending: e.target.value === "1" })}
            >
              <option value="">전체</option>
              <option value="1">승인도 출고도 없는 건</option>
            </select>
          </label>
          <label className="field">
            ★ 교체 시리얼
            <select
              value={filters.swap}
              onChange={(e) => setFilters({ ...filters, swap: e.target.value })}
            >
              <option value="">전체</option>
              <option value="none">아직 없음</option>
              <option value="any">있음</option>
            </select>
          </label>
          <label className="field">
            입고일 이후
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters({ ...filters, from: e.target.value })}
            />
          </label>
          <label className="field">
            입고일 이전
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters({ ...filters, to: e.target.value })}
            />
          </label>
          <button className="primary" onClick={() => setApplied(filters)}>
            검색
          </button>
          <button
            onClick={() => {
              setFilters(EMPTY);
              setApplied(EMPTY);
            }}
          >
            초기화
          </button>
        </div>
      </div>

      {message && <Notice kind="ok">{message}</Notice>}
      <ErrorNotice error={actionError} />
      <ErrorNotice error={list.error} />

      {shipOpen && (
        <BatchShipCard
          cases={(list.data?.items ?? []).filter((c) => selected.has(c.id))}
          companies={companies.data ?? []}
          onClose={() => setShipOpen(false)}
          onDone={(result) => {
            setShipReport(result);
            setShipOpen(false);
            setSelected(new Set());
            setMessage(
              result.blocked.length === 0
                ? `${result.created.length}건 출고를 등록했습니다.`
                : `${result.created.length}건 등록했고, ${result.blocked.length}건은 등록하지 못했습니다.`,
            );
            list.reload();
          }}
          onError={setActionError}
        />
      )}

      {shipReport && shipReport.warnings.length > 0 && (
        <div className="card">
          <h2>
            출고는 됐지만 날짜가 이상합니다
            <span className="sub">막지 않았습니다 — 보고 판단해 주세요</span>
          </h2>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {shipReport.warnings.map((w) => (
              <li key={w.case_id}>
                <a
                  href={`#/cases/${w.case_id}`}
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(`#/cases/${w.case_id}`);
                  }}
                >
                  {w.case_no ?? `케이스 #${w.case_id}`}
                </a>{" "}
                — {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {shipReport && shipReport.blocked.length > 0 && (
        <div className="card">
          <h2>
            등록하지 못한 출고<span className="sub">이유를 확인하고 판단해 주세요</span>
          </h2>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>케이스</th>
                  <th>사유</th>
                </tr>
              </thead>
              <tbody>
                {shipReport.blocked.map((b) => (
                  <tr key={b.case_id}>
                    {/* 사람은 내부 id 로 케이스를 부르지 않는다. 번호를 모를 때만 id 를 쓴다 */}
                    <td className="mono">{b.case_no ?? `#${b.case_id}`}</td>
                    <td>{b.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {mail && <MailDraftCard draft={mail} onClose={() => setMail(null)} />}

      {deleteReport && deleteReport.blocked.length > 0 && (
        <div className="card">
          <h2>
            지우지 않은 건<span className="sub">이유를 확인하고 판단해 주세요</span>
          </h2>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 90 }}>케이스</th>
                  <th>사유</th>
                </tr>
              </thead>
              <tbody>
                {deleteReport.blocked.map((b) => (
                  <tr key={b.case_id}>
                    <td className="mono">{b.case_no ?? `#${b.case_id}`}</td>
                    <td>{b.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="card">
        <h2>
          목록
          <span className="sub">
            {list.data
              ? list.data.items.length < list.data.total
                ? // 잘린 숫자를 전체인 것처럼 보여주지 않는다
                  `${list.data.total}건 중 ${list.data.items.length}건 표시 — 검색으로 좁혀 주세요`
                : `${list.data.total}건`
              : ""}
          </span>
        </h2>

        <div className="row" style={{ marginBottom: 12 }}>
          {can("approval.decide") && (
            <>
              <button
                disabled={selected.size === 0 || busy}
                onClick={() => approveSelected("SHIP_TO_VENDOR")}
              >
                선택 {selected.size}건 승인 — SECUI 이관
              </button>
              <button
                disabled={selected.size === 0 || busy}
                onClick={() => approveSelected("SHIP_TO_OWNER")}
              >
                선택 {selected.size}건 승인 — 협력사 반송
              </button>
            </>
          )}
          <button disabled={selected.size === 0 || busy} onClick={() => makeMail("REPORT_TO_BOSS_SYMPTOM")}>
            선택 {selected.size}건 — 증상 보고 메일
          </button>
          <button
            disabled={selected.size === 0 || busy}
            onClick={() => makeMail("REPORT_TO_BOSS_TRACKING")}
          >
            선택 {selected.size}건 — 송장 보고 메일
          </button>
          {can("shipment.write") && (
            <button
              disabled={selected.size === 0 || busy}
              onClick={() => {
                setShipReport(null);
                setShipOpen(true);
              }}
            >
              선택 {selected.size}건 출고 등록
            </button>
          )}
          {can("case.delete") && (
            <button
              className="danger"
              disabled={selected.size === 0 || busy}
              onClick={deleteSelected}
            >
              선택 {selected.size}건 삭제
            </button>
          )}
          <div className="grow" />
          {/* 모든 목록 화면에 다운로드 버튼을 둔다 (UI-SPEC §1) */}
          {can("export.excel") && (
            <>
              <button disabled={busy} onClick={() => download(false)}>
                엑셀 다운로드 (개인정보 제외)
              </button>
              {can("case.pii") && (
                <button disabled={busy} onClick={() => download(true)}>
                  엑셀 다운로드 (개인정보 포함)
                </button>
              )}
            </>
          )}
        </div>

        <div className="row quick-filters" style={{ margin: "10px 0" }}>
          <div className="chips">
            {([["", "전체"], ...CASE_STATUS_OPTIONS] as Array<[string, string]>).map(([value, label]) => (
              <button
                key={value}
                className={`chip ${applied.status === value ? "active" : ""}`}
                onClick={() => {
                  const next: Filters = { ...filters, status: value };
                  setFilters(next);
                  setApplied(next);
                }}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="grow" />
          <input
            className="quick-input"
            value={quick}
            onChange={(e) => setQuick(e.target.value)}
            placeholder="이 목록에서 거르기 — 번호·시리얼·증상·행선지"
          />
        </div>
        <div className="row quick-filters" style={{ margin: "0 0 10px" }}>
          <span className="muted" style={{ fontSize: 12, marginRight: 2 }}>입고일</span>
          <div className="chips">
            {([["all", "전체"], ["1", "오늘"], ["7", "최근 7일"], ["month", "이번 달"], ["prevMonth", "지난 달"]] as Array<[string, string]>).map(([k, label]) => (
              <button key={k} className={`chip ${rangeKey === k ? "active" : ""}`} onClick={() => applyRange(k)}>
                {label}
              </button>
            ))}
            {rangeKey === "custom" && <span className="chip active">{applied.from || "…"} ~ {applied.to || "…"}</span>}
          </div>
          <div className="grow" />
          <label className="row tight" style={{ fontSize: 12.5, color: "var(--ink-2)" }}>
            <input type="checkbox" checked={groupByDate} onChange={(e) => setGroupByDate(e.target.checked)} />
            날짜별로 묶어 보기
          </label>
        </div>

        {list.loading ? (
          <Busy />
        ) : (
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }} />
                  <th>번호</th>
                  <th>입고일</th>
                  <th>출고일</th>
                  <th>시리얼</th>
                  <th>상태</th>
                  <th>결과</th>
                  <th>증상</th>
                  <th>★ 교체 시리얼</th>
                  <th>재입고</th>
                </tr>
              </thead>
              <tbody>
                {(list.data?.items ?? []).filter((row: Case) => {
                  const q = quick.trim().toUpperCase();
                  if (!q) return true;
                  return [row.case_no, row.serial_norm, row.replacement_serial, row.reported_symptom, row.shipped_to, row.status]
                    .some((v) => (v ?? "").toUpperCase().includes(q));
                }).flatMap((row: Case, i: number, arr: Case[]) => {
                  // 날짜별 보기: 입고일이 바뀌는 자리에 머리줄을 끼운다
                  const head =
                    groupByDate && (i === 0 || arr[i - 1]!.received_at !== row.received_at) ? (
                      <tr key={`d-${row.received_at}`} className="date-head">
                        <td colSpan={11}>
                          <span className="date-head-label">{row.received_at}</span>
                          <span className="muted">
                            {new Intl.DateTimeFormat("ko-KR", { weekday: "short" }).format(new Date(`${row.received_at}T00:00:00`))} ·{" "}
                            {arr.filter((r) => r.received_at === row.received_at).length}건
                          </span>
                        </td>
                      </tr>
                    ) : null;
                  return head ? [head, rowOf(row)] : [rowOf(row)];
                })}
                {list.data?.items.length === 0 && (
                  <tr>
                    <td colSpan={9} className="muted">
                      조건에 맞는 케이스가 없습니다.
                      {/* 0건은 "그런 장비가 없다" 로 읽힌다 — 무엇으로 찾았는지 같이 보여 준다 (C53·C77) */}
                      {applied.serial !== "" && (
                        <div style={{ marginTop: 4 }}>
                          <code>{normalizeSerial(applied.serial).norm}</code> 이 들어간 원 시리얼·교체 시리얼이
                          없습니다. 0건은 "없다" 가 아니라 "못 찾았다" 일 수 있습니다 — 다른 글자로 다시 찾아 보세요.
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

/** 배치 입고 — 시리얼 여러 줄 붙여넣기 → 정규화 미리보기 → 일괄 접수 */
export function IntakeCard({ companies, onDone }: { companies: Company[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [receivedAt, setReceivedAt] = useState(todayKst());
  const [companyId, setCompanyId] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senderContact, setSenderContact] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [result, setResult] = useState<CaseBatchIntakeResponse | null>(null);

  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l !== "");

  /**
   * 한 줄에서 시리얼과 증상을 가른다. 기존 엑셀이 한 행에 시리얼과 `비고` 를 나란히
   * 두었던 것과 같은 모양이다 — 엑셀에서 두 열을 복사하면 탭으로 붙어서 온다.
   *
   * 가르는 자리는 **탭, 또는 공백 두 칸 이상**이다. 시리얼 안에는 공백이 없고
   * (`normalizeSerial` 이 지운다), 증상 문장에는 공백 한 칸이 흔하다. 한 칸으로
   * 가르면 "I/O 에러 발생" 같은 문장이 시리얼로 잘려 들어간다.
   */
  const parsed = lines.map((line) => {
    const at = line.search(/\t| {2,}/);
    const serial = at === -1 ? line : line.slice(0, at);
    const symptom = at === -1 ? "" : line.slice(at).trim();
    return { raw: serial.trim(), symptom };
  });

  // 정규화 미리보기는 서버를 부르지 않는다 — 프론트와 백엔드가 같은 함수를 쓴다
  // (harness/DATA-DICTIONARY.md §2)
  const preview = parsed.map(({ raw, symptom }) => ({
    raw,
    symptom,
    norm: normalizeSerial(raw).norm,
  }));
  const dupes = new Set(
    preview.map((p) => p.norm).filter((n, i, all) => n !== "" && all.indexOf(n) !== i),
  );

  async function submit() {
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const response = await api.intake({
        received_at: receivedAt,
        owner_company_id: Number(companyId),
        sender_name: senderName || null,
        sender_contact: senderContact || null,
        items: parsed.map(({ raw, symptom }) => ({
          serial: raw,
          reported_symptom: symptom === "" ? null : symptom,
        })),
      });
      setResult(response);
      setText("");
      onDone();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <div className="card">
        <div className="row">
          <strong>배치 입고</strong>
          <span className="muted">시리얼을 여러 줄 붙여넣어 한 번에 접수합니다.</span>
          <div className="grow" />
          <button className="primary" onClick={() => setOpen(true)}>
            입고 접수
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <h2>
        배치 입고<span className="sub">시리얼 한 줄에 하나씩</span>
      </h2>

      <ErrorNotice error={error} />
      {result && (
        <Notice kind={result.skipped.length > 0 || result.warnings.length > 0 ? "warn" : "ok"}>
          <div>
            <strong>{result.created.length}건 접수</strong>
            {result.created.length > 0 && (
              <span className="muted">
                {" "}
                ({result.created.map((c) => c.case_no).join(", ")})
              </span>
            )}
          </div>
          {result.warnings.map((w) => (
            <div key={w} style={{ marginTop: 4 }}>
              <strong>확인해 주세요</strong> — {w}
            </div>
          ))}
          {result.reentry_warnings.length > 0 && (
            <div style={{ marginTop: 4 }}>
              재입고 {result.reentry_warnings.length}건 —{" "}
              {result.reentry_warnings.map((w) => w.serial_norm).join(", ")} (이전 케이스에
              연결했습니다)
            </div>
          )}
          {result.skipped.length > 0 && (
            <div style={{ marginTop: 4 }}>
              건너뛴 줄 {result.skipped.length}개 —{" "}
              {result.skipped
                .map(
                  (s) =>
                    `"${s.serial_raw}" (${
                      s.reason === "DUPLICATE_IN_BATCH" ? "이 배치 안 중복" : "빈 값"
                    })`,
                )
                .join(", ")}
            </div>
          )}
        </Notice>
      )}

      <div className="row" style={{ marginBottom: 12 }}>
        <label className="field">
          입고일
          <input type="date" value={receivedAt} onChange={(e) => setReceivedAt(e.target.value)} />
        </label>
        <label className="field">
          협력사 (반송처)
          <select value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
            <option value="">선택</option>
            {companies.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          담당자 (모르면 비워 둡니다)
          <input value={senderName} onChange={(e) => setSenderName(e.target.value)} />
        </label>
        <label className="field">
          연락처 (모르면 비워 둡니다)
          <input value={senderContact} onChange={(e) => setSenderContact(e.target.value)} />
        </label>
      </div>

      <label className="field" style={{ marginBottom: 12 }}>
        시리얼 목록
        <span className="muted" style={{ fontSize: 12 }}>
          한 줄에 하나. <strong>시리얼 뒤에 탭이나 공백 두 칸</strong>을 두고 증상을 적으면
          같이 저장됩니다 — 엑셀에서 시리얼·비고 두 열을 그대로 복사해 붙여넣어도 됩니다.
        </span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={"JTM2UB0130081\tI/O에러 발생 VSI0 초기화 진행완료\nJTM2UB0130992\n…"}
          rows={6}
        />
      </label>

      {preview.length > 0 && (
        <div className="tablewrap" style={{ marginBottom: 12, maxHeight: 240, overflowY: "auto" }}>
          <table>
            <thead>
              <tr>
                <th>입력 그대로</th>
                <th>저장될 값</th>
                <th>증상 (비고)</th>
                <th>확인</th>
              </tr>
            </thead>
            <tbody>
              {preview.map((p, i) => (
                <tr key={i}>
                  <td className="mono">{p.raw}</td>
                  <td className="mono">
                    {p.norm === "" ? <span className="empty">—</span> : p.norm}
                  </td>
                  <td>{p.symptom === "" ? <span className="empty">—</span> : p.symptom}</td>
                  <td>
                    {p.norm === "" ? (
                      <span className="badge" style={{ color: "var(--warn)" }}>
                        빈 값 — 접수되지 않습니다
                      </span>
                    ) : dupes.has(p.norm) ? (
                      <span className="badge" style={{ color: "var(--warn)" }}>
                        중복 — 하나만 접수됩니다
                      </span>
                    ) : (
                      <span className="empty">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="row">
        <button
          className="primary"
          disabled={busy || lines.length === 0 || companyId === ""}
          onClick={submit}
        >
          {busy ? "접수 중…" : `${lines.length}줄 접수`}
        </button>
        <button onClick={() => setOpen(false)}>닫기</button>
      </div>
    </div>
  );
}

/**
 * 만든 초안을 보여 주고 클립보드로 넘긴다 (MAIL-TEMPLATE §1).
 * **발송 버튼은 없다** — 시스템은 초안까지만 만든다 (AGENT.md B4).
 */
function MailDraftCard({ draft, onClose }: { draft: MailDraft; onClose: () => void }) {
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  async function copyBody() {
    setError(null);
    try {
      await copyRichText(draft.body_html, draft.body_text);
      setCopied("본문을 복사했습니다. 그룹웨어 메일쓰기에 그대로 붙여넣으세요.");
    } catch (err) {
      setError(err);
    }
  }

  async function copySubject() {
    setError(null);
    try {
      await navigator.clipboard.writeText(draft.subject);
      setCopied("제목을 복사했습니다.");
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="card">
      <h2>
        메일 초안<span className="sub">보내는 것은 사람이 직접 합니다</span>
      </h2>

      {draft.warnings.map((w) => (
        <Notice key={w} kind="warn">
          {w}
        </Notice>
      ))}
      {copied && <Notice kind="ok">{copied}</Notice>}
      <ErrorNotice error={error} />

      <div className="row" style={{ marginBottom: 10 }}>
        <label className="field grow">
          제목
          <input readOnly value={draft.subject} />
        </label>
        <button onClick={copySubject}>제목 복사</button>
        <button className="primary" onClick={copyBody}>
          본문 복사 (표 서식 그대로)
        </button>
        <button onClick={onClose}>닫기</button>
      </div>

      {/*
        붙여넣기 전에 눈으로 확인할 수 있어야 한다 — 보내는 사람은 사람이다.
        여기 들어오는 HTML 은 우리 서버의 렌더러(`mail_render.ts`)가 만든 것이고,
        표 안의 값은 전부 escapeHtml 을 지난다(그 테스트가 `mail.test.ts` 에 있다).
        외부에서 온 HTML 을 그대로 그리는 곳이 아니다.
      */}
      <div className="tablewrap" dangerouslySetInnerHTML={{ __html: draft.body_html }} />
    </div>
  );
}

/**
 * 일괄 출고 (harness/API-SPEC.md §5-2).
 *
 * 택배 한 묶음은 같은 날 같은 곳으로 가고 송장번호만 건마다 다르다. 그래서 공통값은
 * 한 번, 송장은 여러 줄 붙여넣기다.
 *
 * **붙여넣은 줄과 케이스의 짝을 보내기 전에 보여준다.** 순서가 한 칸 밀리면 엉뚱한
 * 장비에 엉뚱한 송장이 붙는데, 그건 이 시스템이 막으려는 바로 그 종류의 사고다.
 */
function BatchShipCard({
  cases,
  companies,
  onClose,
  onDone,
  onError,
}: {
  cases: Case[];
  companies: Company[];
  onClose: () => void;
  onDone: (result: ShipmentBatchResponse) => void;
  onError: (err: unknown) => void;
}) {
  const [direction, setDirection] = useState<"OUT_VENDOR" | "OUT_OWNER">("OUT_VENDOR");
  const [dest, setDest] = useState("");
  const [shippedAt, setShippedAt] = useState(todayKst());
  const [carrier, setCarrier] = useState("");
  const [trackingText, setTrackingText] = useState("");
  const [busy, setBusy] = useState(false);

  // 빈 줄은 "송장 없음"이다 — 줄 수와 건수가 어긋나지 않게 그대로 센다
  const lines = trackingText === "" ? [] : trackingText.replace(/\r/g, "").split("\n");
  const pairs = cases.map((c, i) => ({
    row: c,
    typed: lines[i] ?? "",
    norm: (lines[i] ?? "").trim() === "" ? null : normalizeTracking(lines[i]!).norm,
  }));
  const countMismatch = lines.length > 0 && lines.length !== cases.length;

  async function submit() {
    setBusy(true);
    onError(null);
    try {
      const result = await api.shipBatch({
        direction,
        dest_company_id: Number(dest),
        shipped_at: shippedAt,
        carrier: carrier.trim() || undefined,
        items: pairs.map((p) => ({
          case_id: p.row.id,
          tracking: p.typed.trim() === "" ? null : p.typed.trim(),
        })),
      });
      onDone(result);
    } catch (err) {
      onError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h2>
        일괄 출고<span className="sub">택배 한 묶음 — 공통값은 한 번, 송장은 건마다</span>
      </h2>

      <div className="row" style={{ marginBottom: 10 }}>
        <label className="field">
          방향
          <select
            value={direction}
            onChange={(e) => setDirection(e.target.value as "OUT_VENDOR" | "OUT_OWNER")}
          >
            <option value="OUT_VENDOR">SECUI 이관</option>
            <option value="OUT_OWNER">협력사 반송</option>
          </select>
        </label>
        <label className="field">
          행선지
          <select value={dest} onChange={(e) => setDest(e.target.value)}>
            <option value="">선택</option>
            {companies.map((c) => (
              <option key={c.id} value={String(c.id)}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          출고일
          <input type="date" value={shippedAt} onChange={(e) => setShippedAt(e.target.value)} />
        </label>
        <label className="field">
          택배사
          <input value={carrier} onChange={(e) => setCarrier(e.target.value)} />
        </label>
      </div>

      <label className="field" style={{ marginBottom: 10 }}>
        송장번호 — 위에서부터 한 줄에 하나 (없으면 빈 줄)
        <textarea
          rows={Math.min(Math.max(cases.length, 3), 10)}
          value={trackingText}
          placeholder={"451-0477-4411\n451-0477-4422\n"}
          onChange={(e) => setTrackingText(e.target.value)}
        />
      </label>

      {countMismatch && (
        <Notice kind="warn">
          고른 건은 <strong>{cases.length}건</strong>인데 붙여넣은 줄은{" "}
          <strong>{lines.length}줄</strong>입니다. 아래 짝이 맞는지 꼭 확인해 주세요 — 한 칸
          밀리면 엉뚱한 장비에 엉뚱한 송장이 붙습니다.
        </Notice>
      )}

      {/* 보내기 전에 짝을 보여준다. 이 표가 이 화면의 본체다 */}
      <div className="tablewrap" style={{ marginBottom: 12 }}>
        <table>
          <thead>
            <tr>
              <th>번호</th>
              <th>시리얼</th>
              <th>입력 그대로</th>
              <th>저장될 값</th>
            </tr>
          </thead>
          <tbody>
            {pairs.map((p) => (
              <tr key={p.row.id}>
                <td>{p.row.case_no}</td>
                <td className="mono">{p.row.serial_norm}</td>
                <td className="mono">{p.typed.trim() || <span className="empty">—</span>}</td>
                <td className="mono">
                  {p.norm ?? <span className="empty">송장 없음</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="row">
        <button className="primary" disabled={busy || dest === ""} onClick={submit}>
          {busy ? "등록 중…" : `${cases.length}건 출고 등록`}
        </button>
        <button disabled={busy} onClick={onClose}>
          닫기
        </button>
        <span className="muted">승인된 건만 나갑니다. 승인이 없는 건은 이유와 함께 남습니다.</span>
      </div>
    </div>
  );
}
